/**
 * Form model + pure conversions for the tournament create/edit pages
 * (admin#66). Kept out of the React components so the shape rules — what
 * counts as "changed", how a GEL amount becomes `fee_minor`, how a deadline
 * becomes an ISO instant — have one home and are unit-testable without
 * rendering anything.
 *
 * The PATCH diffing here matters beyond tidiness: `prospin-api`'s freeze rule
 * (api#155) triggers on **field presence** in the request body, not on
 * whether the value actually changed. Re-sending an unchanged `date` on an
 * already-frozen tournament would draw a 409 `frozen_field` the admin never
 * asked for. `buildPatchTournamentBody` only includes a key when its value
 * actually differs from what was loaded, so a content-only edit of a frozen
 * tournament (translations/image/capacity increase) never touches the
 * frozen keys at all — independent of whatever the UI's own frozen-hint
 * happens to believe.
 */
import type { CreateTournamentBody, PatchTournamentBody } from './api';
import { feeGelFromMinor, feeMinorFromGel, TOURNAMENT_LOCALES, LOCALE_LABELS } from './tournaments';
import { formatLocal } from './time';
import type { AdminTournament, PaymentMode, TournamentLocale, TtarResultInputMode } from './types';

export interface TournamentTranslationForm {
  title: string;
  description: string;
}

export type TournamentTranslationsForm = Record<TournamentLocale, TournamentTranslationForm>;

export interface TournamentFormValue {
  translations: TournamentTranslationsForm;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string; // HH:MM (may be 24:00)
  tableIds: number[];
  feeGel: string; // free text; parsed to fee_minor on submit
  paymentMode: PaymentMode;
  maxParticipants: string; // free text, optional
  minParticipants: string; // free text, optional — TTAR `minParticipants` mirror (api#353)
  minTtarRating: string; // free text, optional
  maxTtarRating: string; // free text, optional
  warmupMinutes: string; // free text; parsed to warmup_minutes on submit
  ttarResultInputMode: TtarResultInputMode;
  registrationClosesDate: string; // optional, YYYY-MM-DD
  registrationClosesTime: string; // optional, HH:MM — paired with the date above
  imageUrl: string;
  cashDisabled: boolean;
}

export const EMPTY_TOURNAMENT_FORM: TournamentFormValue = {
  translations: {
    en: { title: '', description: '' },
    ru: { title: '', description: '' },
    ka: { title: '', description: '' },
  },
  date: '',
  startTime: '',
  endTime: '',
  tableIds: [],
  feeGel: '',
  paymentMode: 'pay_later',
  maxParticipants: '',
  minParticipants: '',
  minTtarRating: '',
  maxTtarRating: '',
  warmupMinutes: '30',
  ttarResultInputMode: 'PlayersWithOrganizerConfirm',
  registrationClosesDate: '',
  registrationClosesTime: '',
  imageUrl: '',
  cashDisabled: false,
};

/** Populate the shared form model from a loaded `AdminTournament` (edit page). */
export function formFromTournament(t: AdminTournament): TournamentFormValue {
  const translations = TOURNAMENT_LOCALES.reduce((acc, locale) => {
    const tr = t.translations[locale];
    acc[locale] = { title: tr?.title ?? '', description: tr?.description ?? '' };
    return acc;
  }, {} as TournamentTranslationsForm);

  return {
    translations,
    date: t.date,
    startTime: t.start_time,
    endTime: t.end_time,
    tableIds: [...t.blocked_table_ids],
    feeGel: String(feeGelFromMinor(t.fee_minor)),
    paymentMode: t.payment_mode,
    maxParticipants: t.max_participants != null ? String(t.max_participants) : '',
    minParticipants: t.min_participants != null ? String(t.min_participants) : '',
    minTtarRating: t.min_ttar_rating != null ? String(t.min_ttar_rating) : '',
    maxTtarRating: t.max_ttar_rating != null ? String(t.max_ttar_rating) : '',
    warmupMinutes: String(t.warmup_minutes),
    ttarResultInputMode: t.ttar_result_input_mode,
    registrationClosesDate: t.registration_closes_at ? formatLocal(t.registration_closes_at, 'yyyy-MM-dd') : '',
    registrationClosesTime: t.registration_closes_at ? formatLocal(t.registration_closes_at, 'HH:mm') : '',
    imageUrl: t.image_url ?? '',
    cashDisabled: t.cash_disabled,
  };
}

/** Human-readable validation errors, or an empty array when the form can submit. */
export function tournamentFormErrors(form: TournamentFormValue): string[] {
  const errors: string[] = [];

  for (const locale of TOURNAMENT_LOCALES) {
    if (!form.translations[locale].title.trim()) {
      errors.push(`${LOCALE_LABELS[locale]} title is required.`);
    }
  }

  if (!form.date) errors.push('Date is required.');
  if (!form.startTime) errors.push('Start time is required.');
  if (!form.endTime) errors.push('End time is required.');
  if (form.startTime && form.endTime && form.endTime <= form.startTime) {
    errors.push('End time must be after start time.');
  }

  if (form.tableIds.length === 0) errors.push('Select at least one table.');

  const fee = Number(form.feeGel);
  if (form.feeGel.trim() === '' || !Number.isFinite(fee) || fee < 0) {
    errors.push('Fee must be a non-negative number.');
  }

  if (form.maxParticipants.trim() !== '') {
    const cap = Number(form.maxParticipants);
    if (!Number.isInteger(cap) || cap <= 0) {
      errors.push('Capacity must be a positive whole number.');
    }
  }

  if (form.minParticipants.trim() !== '') {
    const min = Number(form.minParticipants);
    if (!Number.isInteger(min) || min < 2 || min > 255) {
      errors.push('Min participants must be a whole number between 2 and 255.');
    } else if (form.maxParticipants.trim() !== '' && min > Number(form.maxParticipants)) {
      errors.push('Min participants must not exceed capacity.');
    }
  }

  const ratingBounds = [form.minTtarRating, form.maxTtarRating];
  const hasInvalidRatingBound = ratingBounds.some((value) => {
    if (value.trim() === '') return false;
    const rating = Number(value);
    return !Number.isInteger(rating) || rating < 1;
  });
  if (hasInvalidRatingBound) {
    errors.push('TTAR rating must be a whole number of at least 1');
  } else if (form.minTtarRating.trim() !== '' && form.maxTtarRating.trim() !== '') {
    if (Number(form.minTtarRating) > Number(form.maxTtarRating)) {
      errors.push("TTAR rating 'from' must not exceed 'to'");
    }
  }

  const warmupMinutes = Number(form.warmupMinutes);
  if (
    form.warmupMinutes.trim() === ''
    || !Number.isInteger(warmupMinutes)
    || warmupMinutes < 0
    || warmupMinutes > 120
  ) {
    errors.push('Warmup must be a whole number of minutes between 0 and 120');
  } else if (form.startTime) {
    const [hours, minutes] = form.startTime.split(':').map(Number);
    if (hours * 60 + minutes < warmupMinutes) {
      errors.push('Warmup cannot start before midnight');
    }
  }

  if (form.registrationClosesTime.trim() !== '' && form.registrationClosesDate.trim() === '') {
    errors.push('Registration deadline needs a date, not just a time.');
  }

  return errors;
}

/**
 * A registration deadline as an explicit-offset ISO instant, or null when
 * none was set. The offset is Asia/Tbilisi's fixed +04:00 (no DST) — the same
 * convention `IssueSubscriptionDialog` uses for subscription dates, so an
 * offsetless deadline reads as venue-local regardless of the admin's own
 * device timezone, matching how the API interprets one it receives naked.
 */
function registrationClosesIso(
  form: Pick<TournamentFormValue, 'registrationClosesDate' | 'registrationClosesTime'>,
): string | null {
  if (!form.registrationClosesDate.trim()) return null;
  const time = form.registrationClosesTime.trim() || '00:00';
  return `${form.registrationClosesDate}T${time}:00+04:00`;
}

/** A blank optional number input is `null` on the wire; anything else is sent as a number. */
function nullableInt(value: string): number | null {
  return value.trim() === '' ? null : Number(value);
}

type TranslationsPayload = Record<TournamentLocale, { title: string; description: string | null }>;

function translationsPayload(
  form: TournamentTranslationsForm,
  locales: readonly TournamentLocale[],
): TranslationsPayload {
  return locales.reduce((acc, locale) => {
    acc[locale] = {
      title: form[locale].title.trim(),
      description: form[locale].description.trim() || null,
    };
    return acc;
  }, {} as TranslationsPayload);
}

/** Build the `POST /admin/tournaments` body. Caller validates first. */
export function buildCreateTournamentBody(
  form: TournamentFormValue,
  status: 'draft' | 'published' = 'draft',
): CreateTournamentBody {
  return {
    translations: translationsPayload(form.translations, TOURNAMENT_LOCALES),
    date: form.date,
    start_time: form.startTime,
    end_time: form.endTime,
    fee_minor: feeMinorFromGel(Number(form.feeGel)),
    payment_mode: form.paymentMode,
    table_ids: form.tableIds,
    max_participants: nullableInt(form.maxParticipants),
    min_participants: nullableInt(form.minParticipants),
    min_ttar_rating: nullableInt(form.minTtarRating),
    max_ttar_rating: nullableInt(form.maxTtarRating),
    warmup_minutes: Number(form.warmupMinutes),
    ttar_result_input_mode: form.ttarResultInputMode,
    registration_closes_at: registrationClosesIso(form),
    image_url: form.imageUrl.trim() || null,
    cash_disabled: form.cashDisabled,
    status,
  };
}

function sameNumberSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const as = [...a].sort((x, y) => x - y);
  const bs = [...b].sort((x, y) => x - y);
  return as.every((v, i) => v === bs[i]);
}

function sameInstant(a: string | null, b: string | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return new Date(a).getTime() === new Date(b).getTime();
}

/**
 * Build the `PATCH /admin/tournaments/{id}` body from what actually changed
 * between the loaded form (`original`) and the edited one (`current`). See
 * the module doc for why this is presence-diffed rather than "send
 * everything" — sending an unchanged frozen field would draw a spurious 409.
 */
export function buildPatchTournamentBody(
  original: TournamentFormValue,
  current: TournamentFormValue,
): PatchTournamentBody {
  // The API model deliberately permits sparse PATCH requests, but the
  // generator represents its defaulted fields as required. Keep PATCH sparse
  // so edits do not reset values that this older form does not yet expose.
  const body: Partial<PatchTournamentBody> = {};

  const changedLocales = TOURNAMENT_LOCALES.filter((locale) => {
    const a = original.translations[locale];
    const b = current.translations[locale];
    return a.title !== b.title || a.description !== b.description;
  });
  if (changedLocales.length > 0) {
    body.translations = translationsPayload(current.translations, changedLocales);
  }

  if (current.date !== original.date) body.date = current.date;
  if (current.startTime !== original.startTime) body.start_time = current.startTime;
  if (current.endTime !== original.endTime) body.end_time = current.endTime;
  if (!sameNumberSet(current.tableIds, original.tableIds)) body.table_ids = current.tableIds;

  const currentFeeMinor = feeMinorFromGel(Number(current.feeGel));
  const originalFeeMinor = feeMinorFromGel(Number(original.feeGel));
  if (currentFeeMinor !== originalFeeMinor) body.fee_minor = currentFeeMinor;
  if (current.paymentMode !== original.paymentMode) body.payment_mode = current.paymentMode;

  const currentCap = nullableInt(current.maxParticipants);
  const originalCap = nullableInt(original.maxParticipants);
  if (currentCap !== originalCap) body.max_participants = currentCap;

  const currentMinParticipants = nullableInt(current.minParticipants);
  const originalMinParticipants = nullableInt(original.minParticipants);
  if (currentMinParticipants !== originalMinParticipants) body.min_participants = currentMinParticipants;

  const currentMinTtarRating = nullableInt(current.minTtarRating);
  const originalMinTtarRating = nullableInt(original.minTtarRating);
  if (currentMinTtarRating !== originalMinTtarRating) body.min_ttar_rating = currentMinTtarRating;

  const currentMaxTtarRating = nullableInt(current.maxTtarRating);
  const originalMaxTtarRating = nullableInt(original.maxTtarRating);
  if (currentMaxTtarRating !== originalMaxTtarRating) body.max_ttar_rating = currentMaxTtarRating;

  const currentWarmupMinutes = Number(current.warmupMinutes);
  const originalWarmupMinutes = Number(original.warmupMinutes);
  if (currentWarmupMinutes !== originalWarmupMinutes) body.warmup_minutes = currentWarmupMinutes;

  if (current.ttarResultInputMode !== original.ttarResultInputMode) {
    body.ttar_result_input_mode = current.ttarResultInputMode;
  }

  const currentReg = registrationClosesIso(current);
  const originalReg = registrationClosesIso(original);
  if (!sameInstant(currentReg, originalReg)) body.registration_closes_at = currentReg;

  const currentImg = current.imageUrl.trim() || null;
  const originalImg = original.imageUrl.trim() || null;
  if (currentImg !== originalImg) body.image_url = currentImg;

  if (current.cashDisabled !== original.cashDisabled) body.cash_disabled = current.cashDisabled;

  return body as PatchTournamentBody;
}
